import { makeStyles, mergeClasses } from '@fluentui/react-components';

import { formatHM } from '../../common';
import { DonutChart } from '../../components';
import { palette, tokens, useSurfaceStyles } from '../../theme';
import { computeAllocationFractions, percentOfCapacity } from '../models';
import type { AllocationStats, LegendCounts } from '../use-month-stats';
import { CountChip, useCountChipGridStyles } from './CountChip';

export type AllocationSummaryCardProps = {
    stats: AllocationStats;
    counts: LegendCounts;
};

export const AllocationSummaryCard: React.FC<AllocationSummaryCardProps> = ({ stats, counts }) => {
    const classes = useStyles();
    const chipGrid = useCountChipGridStyles();
    const surfaces = useSurfaceStyles();

    const { savedFrac, pendingFrac, remainingFrac } = computeAllocationFractions(stats.capacity, stats.saved, stats.pending, stats.missing);
    const allocatedPercent = percentOfCapacity(stats.allocated, stats.capacity);

    return (
        <section className={mergeClasses(surfaces.card, classes.card)}>
            <div className={classes.cardTitle}>Allocation summary</div>

            <DonutChart
                size={110}
                strokeWidth={5}
                trackColor={palette.cardBorder}
                segments={[
                    { fraction: savedFrac, color: palette.goodAccent },
                    { fraction: pendingFrac, color: palette.pendingAccent },
                    { fraction: remainingFrac, color: palette.mutedAccent },
                ]}
                className={classes.donutWrap}
            >
                <span className={classes.donutPercent}>{allocatedPercent}%</span>
                <span className={classes.donutPercentLabel}>Allocated</span>
            </DonutChart>

            <div className={classes.legendCol}>
                <LegendLine color={palette.goodAccent} label="Allocated" value={`${formatHM(stats.saved)} h`} tone="good" />
                <LegendLine color={palette.pendingAccent} label="Pending" value={`${formatHM(stats.pending)} h`} tone="warn" />
                <LegendLine color={palette.mutedAccent} label="Remaining" value={`${formatHM(stats.missing)} h`} tone="muted" />
            </div>

            <div className={classes.cardSeparator} aria-hidden />

            <div className={chipGrid.grid}>
                <CountChip color={palette.loggedAccent} label="Issues" count={counts.issues} />
                <CountChip color={palette.bugAccent} label="Bugs" count={counts.bugs} />
                <CountChip color={palette.requestAccent} label="Requests" count={counts.requests} />
            </div>
        </section>
    );
};

type LegendLineProps = {
    color: string;
    label: string;
    value: string;
    tone: 'good' | 'warn' | 'muted';
};

const LegendLine: React.FC<LegendLineProps> = ({ color, label, value, tone }) => {
    const classes = useStyles();
    const valueClass = tone === 'good' ? classes.legendGood : tone === 'warn' ? classes.legendWarn : classes.legendMuted;

    return (
        <div className={classes.legendLine}>
            <span className={classes.legendDot} style={{ backgroundColor: color }} />
            <span className={classes.legendLabel}>{label}</span>
            <span className={mergeClasses(classes.legendValue, valueClass)}>{value}</span>
        </div>
    );
};

const useStyles = makeStyles({
    card: {
        padding: '22px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    cardTitle: {
        fontSize: '16px',
        fontWeight: tokens.fontWeightBold,
        color: tokens.colorNeutralForeground1,
    },
    cardSeparator: {
        height: '1px',
        backgroundColor: palette.cardBorder,
    },
    donutWrap: {
        alignSelf: 'center',
    },
    donutPercent: {
        fontSize: '24px',
        fontWeight: tokens.fontWeightBold,
        color: tokens.colorNeutralForeground1,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
    },
    donutPercentLabel: {
        fontSize: '11.5px',
        color: palette.dimText,
        marginTop: '4px',
    },
    legendCol: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        minWidth: 0,
    },
    legendLine: {
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        alignItems: 'center',
        gap: '10px',
        fontSize: '13px',
        whiteSpace: 'nowrap',
    },
    legendDot: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        flexShrink: 0,
    },
    legendLabel: {
        color: tokens.colorNeutralForeground1,
        fontWeight: tokens.fontWeightSemibold,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    legendValue: {
        fontWeight: tokens.fontWeightBold,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
        // Reserve enough space for HHH:mm h (e.g. "119:05 h") without wrapping.
        minWidth: '64px',
        textAlign: 'right',
    },
    legendGood: { color: palette.goodAccent },
    legendWarn: { color: palette.pendingAccent },
    legendMuted: { color: palette.dimText },
});
